/**
 * Encomendas — GESTÃO (portaria/síndico), 0.1.51.
 *
 * A portaria registra a chegada (gera código de retirada e avisa o apto por e-mail),
 * acompanha o que está aguardando e dá baixa na entrega conferindo o código (PIN).
 * Aberta pelo menu inicial do condomínio.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2, Package, PackageCheck, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { fmtDataHora as fmt } from "../lib/datas";

interface Encomenda {
  id: number; unidade: string; tipo: string | null; remetente: string | null;
  transportadora: string | null; observacao: string | null; status: string;
  codigo_retirada: string | null; recebido_por: string | null; recebido_em: string | null;
  retirado_por_nome: string | null; entregue_por: string | null; retirado_em: string | null;
  retirado_por_terceiro: boolean;
}
interface Resp {
  condominio_id: number; itens: Encomenda[]; pode_gerir: boolean;
  unidades: string[]; tipos: string[]; status_possiveis: string[];
}

const STATUS_LABEL: Record<string, string> = {
  aguardando: "Aguardando", retirada: "Retirada", devolvida: "Devolvida",
};
const STATUS_COR: Record<string, string> = {
  aguardando: "bg-amber-100 text-amber-800", retirada: "bg-emerald-100 text-emerald-800",
  devolvida: "bg-slate-100 text-slate-500",
};
interface FormState { unidade: string; tipo: string; remetente: string; transportadora: string; observacao: string }
const VAZIO: FormState = { unidade: "", tipo: "", remetente: "", transportadora: "", observacao: "" };

export function EncomendasPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const inval = () => qc.invalidateQueries({ queryKey: ["encomendas", condominioId] });
  const [fStatus, setFStatus] = useState("aguardando");
  const { data, isLoading } = useQuery<Resp>({
    queryKey: ["encomendas", condominioId, fStatus],
    queryFn: () => api.get(`/condominios/${condominioId}/encomendas${fStatus ? `?status_f=${fStatus}` : ""}`),
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Retirada: guarda a encomenda-alvo + o PIN digitado + nome de quem retira.
  const [retir, setRetir] = useState<{ e: Encomenda; codigo: string; nome: string; terceiro: boolean } | null>(null);
  const [retErro, setRetErro] = useState<string | null>(null);

  const registrar = useMutation({
    mutationFn: () => {
      const f = form!;
      return api.post(`/condominios/${condominioId}/encomendas`, {
        unidade: f.unidade.trim(), tipo: f.tipo.trim() || null, remetente: f.remetente.trim() || null,
        transportadora: f.transportadora.trim() || null, observacao: f.observacao.trim() || null,
      });
    },
    onSuccess: () => { setForm(null); setErro(null); inval(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível registrar."),
  });
  const retirar = useMutation({
    mutationFn: () => api.post(`/encomendas/${retir!.e.id}/retirar`, {
      codigo: retir!.codigo.trim(), retirado_por_nome: retir!.nome.trim(), terceiro: retir!.terceiro,
    }),
    onSuccess: () => { setRetir(null); setRetErro(null); inval(); },
    onError: (e) => setRetErro(e instanceof ApiError ? e.message : "Não foi possível dar baixa."),
  });
  const devolver = useMutation({
    mutationFn: (id: number) => api.post(`/encomendas/${id}/devolver`, {}),
    onSuccess: () => inval(),
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/encomendas/${id}`),
    onSuccess: () => inval(),
  });

  const itens = data?.itens ?? [];

  return (
    <div className="max-w-2xl mx-auto px-1">
      <div className="flex items-center justify-between pt-2 pb-4">
        <button onClick={onVoltar}
          className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <div className="flex items-center gap-2 text-slate-700 font-semibold"><Package size={18} /> Encomendas — Portaria</div>
      </div>

      {data?.pode_gerir && !form && (
        <button onClick={() => { setForm({ ...VAZIO }); setErro(null); }}
          className="mb-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-700 hover:bg-cyan-800 text-white font-medium">
          <Plus size={18} /> Registrar chegada
        </button>
      )}

      {form && (
        <div className="mb-4 p-4 rounded-xl border border-slate-200 bg-white space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-slate-500 flex flex-col gap-1">Apartamento
              <input list="unidades-enc" value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })}
                placeholder="Ex.: 301" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
              <datalist id="unidades-enc">{(data?.unidades ?? []).map((u) => <option key={u} value={u} />)}</datalist>
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1">Tipo
              <select value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                className="text-sm rounded-md border border-slate-300 px-2 py-2 bg-white">
                <option value="">(opcional)</option>
                {(data?.tipos ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.remetente} onChange={(e) => setForm({ ...form, remetente: e.target.value })}
              placeholder="Remetente (opcional)" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
            <input value={form.transportadora} onChange={(e) => setForm({ ...form, transportadora: e.target.value })}
              placeholder="Transportadora (opcional)" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
          </div>
          <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
            placeholder="Observação (opcional)" rows={2}
            style={{ whiteSpace: "pre-wrap", overflowWrap: "break-word" }}
            className="w-full text-sm rounded-md border border-slate-300 px-3 py-2 resize-y" />
          {erro && <div className="text-sm text-rose-700">{erro}</div>}
          <div className="flex gap-2">
            <button onClick={() => registrar.mutate()} disabled={registrar.isPending || !form.unidade.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-sm disabled:opacity-50">
              {registrar.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Registrar e avisar
            </button>
            <button onClick={() => { setForm(null); setErro(null); }}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">Cancelar</button>
          </div>
          <p className="text-xs text-slate-400">Ao registrar, o sistema gera um código de retirada e envia um e-mail ao morador do apto.</p>
        </div>
      )}

      <div className="flex gap-1.5 mb-3">
        {["aguardando", "retirada", "devolvida", ""].map((s) => (
          <button key={s || "todas"} onClick={() => setFStatus(s)}
            className={`text-xs px-2.5 py-1.5 rounded-md border ${fStatus === s ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200"}`}>
            {s ? STATUS_LABEL[s] : "Todas"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 text-slate-500 py-14"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
      ) : itens.length === 0 ? (
        <div className="text-center text-slate-400 py-14 text-sm">Nenhuma encomenda {fStatus ? STATUS_LABEL[fStatus].toLowerCase() : ""}.</div>
      ) : (
        <div className="space-y-2.5">
          {itens.map((e) => (
            <div key={e.id} className="p-4 rounded-xl border border-slate-200 bg-white">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 flex items-center gap-2">
                    <Package size={16} className="text-cyan-700 flex-shrink-0" /> Apto {e.unidade}
                    {e.tipo && <span className="text-xs font-normal text-slate-500">· {e.tipo}</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Chegou em {fmt(e.recebido_em)}{e.recebido_por ? ` · por ${e.recebido_por}` : ""}
                    {e.remetente && <span> · de {e.remetente}</span>}
                    {e.transportadora && <span> · {e.transportadora}</span>}
                  </div>
                  {e.observacao && <div className="text-sm text-slate-600 mt-1">{e.observacao}</div>}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_COR[e.status] ?? "bg-slate-100"}`}>{STATUS_LABEL[e.status] ?? e.status}</span>
                    {e.status === "aguardando" && e.codigo_retirada && (
                      <span className="text-[11px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-800 font-mono">código {e.codigo_retirada}</span>
                    )}
                    {e.status === "retirada" && (
                      <span className="text-[11px] text-slate-500">Entregue a {e.retirado_por_nome}{e.retirado_por_terceiro ? " (autorizado)" : ""} em {fmt(e.retirado_em)}</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {e.status === "aguardando" && (
                    <>
                      <button title="Dar baixa (entregar)" onClick={() => { setRetErro(null); setRetir({ e, codigo: "", nome: "", terceiro: false }); }}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs"><PackageCheck size={14} /> Entregar</button>
                      <button title="Devolver ao remetente" onClick={() => { if (confirm("Marcar como devolvida ao remetente?")) devolver.mutate(e.id); }}
                        className="text-[11px] px-2 py-1 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50">Devolver</button>
                    </>
                  )}
                  <button title="Excluir (corrigir lançamento)" onClick={() => { if (confirm("Excluir esta encomenda?")) excluir.mutate(e.id); }}
                    className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 self-end"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {retir && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setRetir(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm" onClick={(ev) => ev.stopPropagation()}>
            <h3 className="font-bold text-slate-900 mb-1">Entregar encomenda — Apto {retir.e.unidade}</h3>
            <p className="text-xs text-slate-500 mb-3">Peça o <b>código de retirada</b> que está no app do morador (ou no e-mail).</p>
            <label className="text-xs text-slate-500 flex flex-col gap-1 mb-2">Código de retirada
              <input value={retir.codigo} onChange={(e) => setRetir({ ...retir, codigo: e.target.value })}
                inputMode="numeric" placeholder="ex.: 0423" className="text-lg tracking-widest font-mono rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-xs text-slate-500 flex flex-col gap-1 mb-2">Quem está retirando
              <input value={retir.nome} onChange={(e) => setRetir({ ...retir, nome: e.target.value })}
                placeholder="Nome de quem retira" className="text-sm rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
              <input type="checkbox" checked={retir.terceiro} onChange={(e) => setRetir({ ...retir, terceiro: e.target.checked })} />
              Retirada por terceiro autorizado (não é o morador)
            </label>
            {retErro && <div className="text-sm text-rose-700 mb-2">{retErro}</div>}
            <div className="flex gap-2">
              <button onClick={() => retirar.mutate()} disabled={retirar.isPending || !retir.codigo.trim() || !retir.nome.trim()}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50">
                {retirar.isPending ? <Loader2 size={16} className="animate-spin" /> : <PackageCheck size={16} />} Confirmar entrega
              </button>
              <button onClick={() => setRetir(null)} className="px-4 py-2 rounded-md border border-slate-300 text-slate-600 text-sm hover:bg-slate-50">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
